/**
 * Download utilities for DEX reader workflow.
 * Handles downloading decrypted content and keypairs.
 */

/**
 * Download a Blob as a file.
 *
 * @param {Blob} blob - Blob to download
 * @param {string} filename - Filename for the download
 */
export function downloadBlob(blob, filename) {
  // Create object URL
  const url = URL.createObjectURL(blob);

  // Create anchor element for download
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;

  // Trigger download
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Cleanup object URL after a short delay
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Download decrypted content as a file.
 *
 * @param {Uint8Array} plaintextBytes - Decrypted content bytes
 * @param {string} [filename='decrypted.bin'] - Filename for the download
 */
export function downloadDecrypted(plaintextBytes, filename = 'decrypted.bin') {
  const blob = new Blob([plaintextBytes], { type: 'application/octet-stream' });
  downloadBlob(blob, filename);
}

/**
 * Download a keypair as two separate text files.
 * Downloads public key first, then private key after a short delay.
 *
 * @param {string} publicKey - Base64-encoded public key
 * @param {string} privateKey - Base64-encoded private key
 * @param {string} [prefix='reader'] - Filename prefix
 */
export function downloadKeypair(publicKey, privateKey, prefix = 'reader') {
  // Download public key
  const publicBlob = new Blob([publicKey], { type: 'text/plain' });
  downloadBlob(publicBlob, `${prefix}_public_key.txt`);

  // Download private key after a short delay to prevent browser issues
  setTimeout(() => {
    const privateBlob = new Blob([privateKey], { type: 'text/plain' });
    downloadBlob(privateBlob, `${prefix}_private_key.txt`);
  }, 100);
}
